import { FileTextIcon, UploadIcon } from 'lucide-react';
import { type DragEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../shared/lib/cn';
import { Button } from '../../../shared/ui/button';
import { Field } from '../../../shared/ui/field';
import { Input } from '../../../shared/ui/input';
import { toast } from '../../../shared/ui/sonner';
import { useUploadVersion, type VersionRefusal } from './model';

const REFUSAL_KEYS = {
  not_pdf: 'versions.upload.notPdf',
  too_large: 'versions.upload.tooLarge',
  unpriceable: 'versions.upload.unpriceable',
  limit: 'versions.upload.limit',
  label: 'versions.upload.labelTooLong',
} as const satisfies Record<VersionRefusal, string>;

/**
 * The upload part of the Versions card: an optional label, then a drop zone. The upload starts as soon as a file
 * is chosen or dropped, and the customer waits on "Reading your version…". Refusals are worded as the original
 * script's are, with "version" in place of "script".
 */
export function UploadVersion({ projectId }: { projectId: string }) {
  const { t } = useTranslation('project');
  const { state, upload, retry, busy } = useUploadVersion(projectId);
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [label, setLabel] = useState('');
  const headingId = useId();

  async function choose(file: File | undefined) {
    if (input.current) input.current.value = '';
    if (!file || busy) return;
    if (await upload(file, label)) {
      setLabel('');
      toast.success(t('versions.upload.done'));
    }
  }
  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setOver(false);
    void choose(event.dataTransfer.files[0]);
  }

  return (
    <section aria-labelledby={headingId} className="grid gap-4">
      <div className="grid gap-1">
        <h3 id={headingId} className="font-bold text-heading">
          {t('versions.upload.title')}
        </h3>
        <p className="text-sm text-muted-foreground">{t('versions.upload.help')}</p>
      </div>
      <Field
        label={t('versions.upload.labelField')}
        description={t('versions.upload.labelHelp')}
        error={
          state.phase === 'refused' && state.reason === 'label'
            ? t('versions.upload.labelTooLong')
            : undefined
        }
      >
        <Input value={label} disabled={busy} onChange={(event) => setLabel(event.target.value)} />
      </Field>
      {busy ? (
        <div
          role="status"
          className="grid justify-items-center gap-2 rounded-sm border border-dashed border-input px-4 py-8 text-center"
        >
          <FileTextIcon aria-hidden="true" className="size-8 animate-pulse text-primary" />
          <p className="font-bold text-heading">
            {state.phase === 'uploading'
              ? t('versions.upload.uploading')
              : t('versions.upload.reading')}
          </p>
          <p className="text-sm text-muted-foreground">{t('versions.upload.readingHelp')}</p>
        </div>
      ) : (
        // biome-ignore lint/a11y/noStaticElementInteractions: a drop target; the file input inside it is the control
        <div
          data-over={over}
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
          className={cn(
            'grid justify-items-center gap-3 rounded-sm border border-dashed border-input px-4 py-6 text-center transition-colors duration-150',
            over && 'border-primary bg-accent',
          )}
        >
          <UploadIcon aria-hidden="true" className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {over ? t('versions.upload.dropActive') : t('versions.upload.drop')}
          </p>
          <Input
            ref={input}
            type="file"
            accept="application/pdf,.pdf"
            aria-label={t('versions.upload.chooseLabel')}
            className="sr-only"
            tabIndex={-1}
            onChange={(event) => void choose(event.target.files?.[0])}
          />
          <Button variant="secondary" onClick={() => input.current?.click()}>
            <UploadIcon aria-hidden="true" />
            {t('versions.upload.choose')}
          </Button>
        </div>
      )}
      {state.phase === 'refused' && state.reason !== 'label' ? (
        <div role="alert" className="grid gap-1 text-sm">
          <p className="text-destructive">{t(REFUSAL_KEYS[state.reason])}</p>
          {state.reason === 'limit' ? null : (
            <p className="text-muted-foreground">{t('versions.upload.another')}</p>
          )}
        </div>
      ) : null}
      {state.phase === 'failed' ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
          <p className="text-destructive">
            {t('versions.upload.failed')} {state.message}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void retry()}>
            {t('versions.upload.retry')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
